import { useTranslation } from "react-i18next";
import { Globe, Check } from "lucide-react";
import { useState, useRef } from "react";
import { LANGUAGES } from "../i18n";
import { PortalDropdown } from "./PortalDropdown";

/**
 * Compact dropdown language switcher. Persists the choice in localStorage
 * via i18next-browser-languagedetector.
 * Uses PortalDropdown to avoid being clipped by parent overflow.
 */
export default function LanguageSwitcher({ compact = false, className = "" }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const pick = async (code) => {
    try { localStorage.setItem("blitzz_lang", code); } catch (_) {}
    setOpen(false);
    if (code === i18n.language) return;
    // Mémorisation dans le profil (web + app) puis rechargement : tous les libellés (y compris constantes) suivent la langue
    try {
      const csrf = (document.cookie.match(/(?:^|; )csrf_token=([^;]+)/) || [])[1];
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/language`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": decodeURIComponent(csrf) } : {}) }, body: JSON.stringify({ language: code }) });
    } catch (_) {}
    await i18n.changeLanguage(code);
    window.location.reload();
  };

  return (
    <div className={"relative " + className} data-testid="language-switcher">
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className={"flex items-center gap-1.5 text-xs font-bold rounded-lg border border-white/10 hover:border-white/20 transition-colors " + (compact ? "px-2 py-1 bg-transparent text-slate-400 hover:text-white" : "px-3 py-1.5 bg-white/5 text-slate-300")}
        data-testid="language-switcher-button"
      >
        <Globe className="w-3.5 h-3.5" />
        <span className="text-sm leading-none">{current.flag}</span>
        {!compact && <span className="uppercase">{current.code}</span>}
      </button>
      <PortalDropdown open={open} onClose={() => setOpen(false)} triggerRef={triggerRef} width={176}>
        <div data-testid="language-switcher-menu">
          {LANGUAGES.map((l) => (
            <button
              key={l.code}
              onClick={() => pick(l.code)}
              className={"w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-white/5 transition-colors " + (l.code === i18n.language ? "text-[#f7c935]" : "text-slate-300")}
              data-testid={"language-option-" + l.code}
            >
              <span className="text-sm">{l.flag}</span>
              <span className="flex-1">{l.label}</span>
              {l.code === i18n.language && <Check className="w-3 h-3" />}
            </button>
          ))}
        </div>
      </PortalDropdown>
    </div>
  );
}
